import { regionCountryMasterApi } from '@api/region-country-master';
import { Chip, TextField } from '@material-ui/core';
import CircularProgress from '@material-ui/core/CircularProgress';
import CancelIcon from '@material-ui/icons/Cancel';
import { Autocomplete } from '@material-ui/lab';
import React, { useEffect, useMemo, useState } from 'react';

export type MarketOption = {
  name: string;
  type: 'region' | 'country';
};

const chipStyle = (type: MarketOption['type']) =>
  type === 'region'
    ? { backgroundColor: '#EDE7F6', color: '#5E35B1' }
    : { backgroundColor: '#E8F5E9', color: '#2E7D32' };

export const asRowList = (response: any): any[] => {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.data?.data)) return response.data.data;
  if (Array.isArray(response?.data)) return response.data;
  return [];
};

export const toOptions = (rows: any): MarketOption[] => {
  const list = Array.isArray(rows) ? rows : [];
  const seen = new Set<string>();
  const options: MarketOption[] = [];

  list.forEach(row => {
    const region = row?.region?.trim();
    if (region && !seen.has(`region:${region.toLowerCase()}`)) {
      seen.add(`region:${region.toLowerCase()}`);
      options.push({ name: region, type: 'region' });
    }

    const countries = Array.isArray(row?.country_name)
      ? row.country_name
      : row?.country_name
        ? [row.country_name]
        : [];
    countries.forEach(country => {
      const name = String(country || '').trim();
      const key = `country:${name.toLowerCase()}`;
      if (name && !seen.has(key)) {
        seen.add(key);
        options.push({ name, type: 'country' });
      }
    });
  });

  return options.sort((a, b) => a.name.localeCompare(b.name));
};

export const normalizeMarkets = (value: any, catalog: MarketOption[] = []): MarketOption[] => {
  const items = Array.isArray(value)
    ? value
    : typeof value === 'string' && value
      ? value.split(',')
      : [];
  const markets: MarketOption[] = [];

  items.forEach(item => {
    const name = String(item?.name ?? item ?? '').trim();
    if (!name) return;
    const match = catalog.find(option => option.name.toLowerCase() === name.toLowerCase());
    const type = item?.type === 'region' || item?.type === 'country' ? item.type : match?.type;
    markets.push({ name: match?.name ?? name, type: type || 'country' });
  });

  return markets;
};

export const toMarketsPayload = (value: any) =>
  normalizeMarkets(value)
    .map(item => item.name)
    .filter(Boolean)
    .join(',');

export function MarketsResponsibleField({
  value,
  onChange,
}: {
  value: MarketOption[] | string | any;
  onChange: (value: string) => void;
}) {
  const [options, setOptions] = useState<MarketOption[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    const subscription = regionCountryMasterApi.filterCatalog().subscribe({
      next: (response: any) => {
        setOptions(toOptions(asRowList(response)));
      },
      error: () => setOptions([]),
      complete: () => setLoading(false),
    });
    return () => subscription.unsubscribe();
  }, []);

  const selected = useMemo(() => normalizeMarkets(value, options), [value, options]);

  return (
    <Autocomplete
      multiple
      filterSelectedOptions
      options={options}
      loading={loading}
      value={selected}
      getOptionLabel={option => option?.name ?? ''}
      getOptionSelected={(option, selectedOption) =>
        option.name === selectedOption.name && option.type === selectedOption.type
      }
      onChange={(_, nextValue) =>
        onChange(
          toMarketsPayload(
            nextValue.filter((item): item is MarketOption => typeof item !== 'string')
          )
        )
      }
      renderOption={option => (
        <span style={{ color: chipStyle(option.type).color, fontWeight: 600 }}>{option.name}</span>
      )}
      renderTags={(selectedOptions, getTagProps) =>
        selectedOptions.map((option, index) => {
          const style = chipStyle(option.type);
          return (
            <Chip
              {...getTagProps({ index })}
              key={`${option.type}-${option.name}`}
              label={option.name}
              size="small"
              deleteIcon={<CancelIcon style={{ color: style.color }} />}
              style={{ ...style, fontWeight: 600 }}
            />
          );
        })
      }
      renderInput={params => (
        <TextField
          {...params}
          label="Markets Responsible"
          variant="outlined"
          placeholder="Type a region or country"
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <React.Fragment>
                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </React.Fragment>
            ),
          }}
        />
      )}
    />
  );
}
